<template>
  <Modal class="notion-add-account" v-bind="$attrs" :is-displayed="isDisplayed">
    <template #title>
      <h3>Connecting your account</h3>
    </template>

    <Spinner
      v-if="!integrationStatus || integrationStatus === 'synchronizing'"
    />

    <div v-if="!integrationStatus" class="buttons">
      <BaseButton
        kind="main"
        type="tertiary"
        text="Cancel"
        @click="$emit('close')"
      />
    </div>

    <template v-else-if="integrationStatus === 'broken'">
      <BaseAlert state="error">
        An error occured with your token:
        {{
          integration?.status.status === "broken"
            ? integration?.status?.error
            : ""
        }}
      </BaseAlert>

      <div class="buttons">
        <BaseButton
          kind="main"
          type="tertiary"
          text="Cancel"
          @click="$emit('close')"
        />
      </div>
    </template>

    <template v-else-if="integrationStatus === 'ready'">
      <BaseAlert state="default"> Account connected. </BaseAlert>

      <label for="user">Select your user</label>
      <VSelect
        v-model="selectedUser"
        :append-to-body="true"
        :clearable="false"
        :disabled="!peopleList || peopleList.length === 1"
        label="name"
        :options="peopleList"
        :placeholder="'Select ...'"
        :searchable="false"
        class="select"
      >
        <template #open-indicator="{ attributes }">
          <span v-bind="attributes"><ChevronDownIcon /></span>
        </template>
      </VSelect>

      <div class="buttons">
        <BaseButton
          kind="main"
          type="tertiary"
          text="Cancel"
          @click="$emit('close')"
        />

        <BaseButton
          kind="main"
          type="primary"
          text="Connect"
          @click="addUser"
        />
      </div>
    </template>
  </Modal>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  Ref,
  ref,
  toRefs,
  watch,
  watchEffect,
} from "vue";

import type {
  IntegrationReport,
  NotionConfig,
  NotionPeople,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import assert from "@/assert";
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import { bindAsync, releasableRef, useReleaser } from "@/ref";

import * as inject from "./inject";
import Modal from "./Modal.vue";

export default defineComponent({
  name: "NotionAddAccount",
  components: {
    BaseAlert,
    BaseButton,
    ChevronDownIcon,
    Modal,
  },
  props: {
    isDisplayed: {
      type: Boolean,
      default: false,
    },
    token: {
      type: String,
      default: null,
    },
  },
  emits: {
    close: () => true,
  },
  async setup(props, { emit }) {
    const { isDisplayed, token } = toRefs(props);
    const controller = inject.controller();
    const integrationId: Ref<string | null> = ref(null);
    const connect = async () => {
      if (token.value)
        integrationId.value = await controller.integration.add({
          secret: token.value,
          type: "notion",
          imports: [],
          people: [],
          databases: [],
          status: { status: "synchronizing" },
        });
    };
    watch(isDisplayed, (value) => {
      if (value) connect();
    });
    const selectedUser: Ref<NotionPeople | null> = ref(null);
    const addUser = () => {
      assert(selectedUser.value !== null);
      assert(integrationId.value !== null);

      controller.integration.update(integrationId.value, {
        self: selectedUser.value.id,
        secret: token.value,
        type: "notion",
        imports: [],
        people: [],
        databases: [],
        status: { status: "synchronizing" },
      });
      emit("close");
    };
    const releaser = useReleaser();
    const integration = await releaser.add(
      bindAsync(
        integrationId,
        async (
          id
        ): Promise<ReleasableRef<NotionConfig<IntegrationReport> | null>> => {
          if (id) {
            const i = await controller.integration.get(id);
            return i.map((value) => (value?.type === "notion" ? value : null));
          } else return releasableRef(null);
        }
      )
    );
    const peopleList = computed(() => {
      return integration.value && integration.value.status?.status === "ready"
        ? integration.value.people
        : null;
    });
    releaser.defer(
      watchEffect(() => {
        if (
          peopleList.value &&
          peopleList.value.length === 1 &&
          selectedUser.value?.id !== peopleList.value[0].id
        ) {
          selectedUser.value = peopleList.value[0];
          addUser();
        }
      })
    );
    const integrationStatus = computed(() => {
      return integration.value?.status?.status;
    });
    return {
      addUser,
      integration,
      integrationStatus,
      peopleList,
      selectedUser,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "../styles/settings.scss";

.notion-add-account {
  display: flex;
  flex-direction: column;

  :deep(.modal) {
    padding: 24px 32px 48px 32px;
    min-width: 511px;

    .modal-wrapper {
      display: flex;
      flex-direction: column;
    }
  }

  h3 {
    margin: 0;
  }

  h3.base-title {
    margin: 0 0 24px 0;
  }

  .token-header {
    display: flex;
    justify-content: space-between;

    .token-helper {
      color: $blue;
    }
  }

  label[for="token"],
  label[for="user"] {
    @extend %font-caption-regular;
    text-transform: uppercase;
    @include themed() {
      color: t($grey-60);
    }
  }

  label[for="user"] {
    margin-top: 24px;
  }

  input[type="text"] {
    padding-block: 12px;
    padding-inline-end: 64px;
    padding-inline-start: 16px;
    margin: 12px 0 24px;
    border-radius: 3px;

    @include themed() {
      color: t($primary);
      background: t($bg);
      border: 1px solid t($border-button);
    }

    &:disabled {
      opacity: 0.5;
    }
  }

  .buttons {
    display: flex;
    justify-content: space-between;
  }

  .select {
    margin: 12px 0 24px;
  }

  .error {
    color: $red;
    margin-bottom: 24px;
  }
}
</style>
